import { AtlasAttachmentLoader, SkeletonJson, Spine, TextureAtlas, } from "@pixi-spine/all-4.0";
import gsap from "gsap";
import * as PIXI from "pixi.js";
import { Dictionary } from "../../util/collection/Dictionary";
import { Logger } from "../../util/log/Logger";
/**
 * SpineData.ts
 */
export class SpineData {
    constructor(spineAtlas, spineData) {
        this.atlas = spineAtlas;
        this.data = spineData;
    }
    /**
     * Loads atlas images and constructs a SpineData object from skeleton and atlas JSONS
     * @param atlasData - .atlas file as a text string
     * @param rawSkeletonData - skeleton data as a JSON/Object
     * @param path - filepath including version
     * @param scale - optional scale param to change skeleton scale on construction.
     */
    static from(atlasData, rawSkeletonData, path, scale) {
        const _path = path || "version/res/spine";
        const _scale = typeof scale === "number" ? scale : 1.0;
        let atlas = new TextureAtlas(atlasData, function (line, callback) {
            let tex = PIXI.utils.BaseTextureCache[_path + line] ||
                PIXI.BaseTexture.from(_path + line);
            callback(tex);
        });
        let spineAtlasLoader = new AtlasAttachmentLoader(atlas);
        let spineJsonParser = new SkeletonJson(spineAtlasLoader);
        spineJsonParser.scale = _scale;
        let data = spineJsonParser.readSkeletonData(rawSkeletonData);
        return new SpineData(atlas, data);
    }
}
/**
 * ILazySpine.ts
 */
export class ILazySpine {
}
/**
 * SpineManager.ts
 */
export class SpineManager {
    constructor() {
        if (SpineManager.instance) {
            throw new Error("SpineCache Singleton Already Initialised");
        }
        this.create();
        SpineManager.instance = this;
    }
    create() {
        this.map = new Dictionary();
        this.preloadMap = new Dictionary();
        this.waitQueue = [];
        this.shimSpineAutoUpdate();
        Logger.addObjectRef(this, "SpineManager");
    }
    shimSpineAutoUpdate() {
        /**
         * pixi-spine has a known issue where adding a spine to the scene and setting the animation can cause the animation to not play
         * as the spines are updated in the autoUpdateTransform function, while pixi loops through its scene children in the updateTransform function.
         * (see: https://github.com/pixijs/spine/issues/159
         *
         * We can resolve this by patching the Spine autoUpdate function to trigger with the gsap ticker.
         **/
        const ticker = gsap.ticker;
        Object.defineProperties(Spine.prototype, {
            autoUpdate: {
                get: function () {
                    return this._autoUpdate;
                },
                set: function (value) {
                    this.updateTransform =
                        PIXI.Container.prototype.updateTransform;
                    if (value !== this._autoUpdate) {
                        if (value) {
                            ticker.add(this.updateTick.bind(this));
                        }
                        else {
                            ticker.remove(this.updateTick.bind(this));
                        }
                    }
                    this._autoUpdate = value;
                },
                enumerable: true,
            },
            updateTick: {
                value: function (time, deltaTime, frame) {
                    if (this.destroyed) {
                        this.autoUpdate = false;
                        return;
                    }
                    const deltaS = deltaTime * gsap.globalTimeline.timeScale() * 0.001;
                    this.update(deltaS);
                },
            },
        });
    }
    preload(url, data) {
        this.preloadMap.setValue(url, data);
        for (let i = 0; i < this.waitQueue.length; i++) {
            const queued = this.waitQueue.pop();
            url === queued.atlasUrl && (queued.atlas = data);
            url === queued.skeletonUrl && (queued.skeleton = data);
            if (queued.atlas && queued.skeleton) {
                const path = queued.atlasUrl.substring(0, queued.atlasUrl.lastIndexOf("/") + 1);
                this.map.setValue(queued.resourceUrl, SpineData.from(queued.atlas, queued.skeleton, path, queued.scale));
            }
            else {
                this.waitQueue.unshift(queued);
            }
        }
    }
    addLazyLoaded(resourceUrl, atlasUrl, skeletonUrl, scale) {
        const atlas = this.preloadMap.getValue(atlasUrl);
        const skeleton = this.preloadMap.getValue(skeletonUrl);
        if (atlas && skeleton) {
            this.map.setValue(resourceUrl, SpineData.from(atlas, skeleton, atlasUrl.substring(0, atlasUrl.lastIndexOf("/") + 1), scale));
        }
        else {
            this.waitQueue.push({
                resourceUrl: resourceUrl,
                atlasUrl: atlasUrl,
                skeletonUrl: skeletonUrl,
                atlas: atlas,
                skeleton: skeleton,
                scale: scale,
            });
        }
    }
    add(resourceUrl, spineAtlas, spineData) {
        this.map.setValue(resourceUrl, new SpineData(spineAtlas, spineData));
        //add sound logic here
    }
    get(resourceUrl) {
        return this.map.getValue(resourceUrl);
    }
    remove(resourceUrl) {
        this.map.remove(resourceUrl);
    }
    static getInstance() {
        if (SpineManager.instance === null) {
            SpineManager.instance = new SpineManager();
        }
        return SpineManager.instance;
    }
}
SpineManager.instance = null;
//# sourceMappingURL=SpineManager.js.map